import { useEffect, useMemo, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { authStore, clearStoredSession } from './session-store';
import { loadAuthConfig } from './auth-config';
import { loadMockAuth, isMockAuthAvailable } from './mock-auth-loader';
import type { MockAuthUser } from './mock-auth';
import { extractRoles } from './extract-roles';
import { setCurrentToken, getJwtExp } from './token-store';
import { safeReturnTo } from './safe-return-to';
import { clearReturnToStash, stashReturnTo, takeReturnToStash } from './return-to-stash';
import { getRuntimeConfig } from '../config/runtime-config';
import { AuthContext } from './AuthContext';
import type { AuthContextValue, AuthMode, AuthenticatedUser, RoleToken } from './types';
import { getRmfLive } from '../api/zenoh-sse';

const SESSION_STORAGE_KEY = 'nexsoss:oidc-user-v1';

/**
 * Where a completed sign-in should land: the round-tripped OIDC `state`
 * when it carries a path, else the tab's stash (return-to-stash.ts says
 * why both exist). Undefined when neither does; the caller falls back.
 *
 * The stash is TAKEN even when the state wins: it belonged to this
 * sign-in, and left behind it was what a reload of the callback, or any
 * later stored-user fallback in this tab, returned again. A callback that
 * fails never gets here and leaves it; the next `login()` overwrites it.
 */
function signinReturnPath(state: unknown): string | undefined {
  const stashed = takeReturnToStash();
  if (typeof state === 'string' && state) return state;
  return stashed ?? undefined;
}

/**
 * The path to come back to after a forced re-sign-in: where the person
 * is NOW, query included. It used to be a hard-coded `/overview`, which
 * threw away the floor and object a locate link had landed them on.
 * `LoginScreen` passes it through `safeReturnTo`, like any other.
 */
function loginUrlForCurrentPage(): string {
  const here = window.location.pathname + window.location.search;
  return `/login?returnTo=${encodeURIComponent(here)}`;
}

function toAuthenticatedUser(user: User | MockAuthUser): AuthenticatedUser {
  const profile = (user as User).profile ?? (user as MockAuthUser);
  const roles = extractRoles(user);
  return {
    id: String((profile as { sub?: string }).sub ?? (user as MockAuthUser).id),
    email: String((profile as { email?: string }).email ?? ''),
    displayName:
      String((profile as { name?: string }).name ?? '') ||
      String((profile as { email?: string }).email ?? '') ||
      'Unknown user',
    roles,
    organisation:
      String((profile as { org?: string }).org ?? (user as MockAuthUser).organisation ?? 'unknown'),
  };
}

export function ZitadelProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const runtime = getRuntimeConfig();
  const authMode: AuthMode = runtime.authMode;

  const userManagerRef = useMemo<UserManager | null>(() => {
    if (authMode !== 'zitadel') return null;
    const cfg = loadAuthConfig();
    return new UserManager({
      authority: cfg.authority,
      client_id: cfg.clientId,
      redirect_uri: cfg.redirectUri,
      post_logout_redirect_uri: cfg.postLogoutRedirectUri,
      response_type: 'code',
      scope: cfg.scope,
      loadUserInfo: true,
      // `localStorage` when installed to a home screen, `sessionStorage`
      // in a browser tab -- see auth/session-store.ts for why the two
      // differ and why the choice is made once at load.
      userStore: new WebStorageStateStore({ store: authStore() }),
      metadata: {
        issuer: cfg.authority,
        authorization_endpoint: `${cfg.authority}/oauth/v2/authorize`,
        token_endpoint: `${cfg.authority}/oauth/v2/token`,
        end_session_endpoint: `${cfg.authority}/oidc/v1/end_session`,
        jwks_uri: `${cfg.authority}/oauth/v2/keys`,
        // `loadUserInfo: true` below requires this — without it,
        // oidc-client-ts throws "Metadata does not contain property
        // userinfo_endpoint" during the post-token-exchange userinfo
        // fetch. This hand-written metadata object bypasses OIDC
        // discovery entirely (no request to
        // `${authority}/.well-known/openid-configuration`), so every
        // endpoint ZITADEL exposes has to be listed here explicitly;
        // this one was missing. Path matches ZITADEL's documented
        // OIDC userinfo endpoint convention (same `/oidc/v1/` prefix
        // as `end_session_endpoint` above).
        userinfo_endpoint: `${cfg.authority}/oidc/v1/userinfo`,
      },
    });
  }, [authMode]);

  useEffect(() => {
    let active = true;
    async function init() {
      try {
        if (authMode === 'mock') {
          if (!isMockAuthAvailable()) {
            console.error(
              'runtime-config.json sets authMode=mock but this is a production build; mock-auth is not available.',
            );
            setCurrentToken(null);
            return;
          }
          const mock = await loadMockAuth();
          const persisted = mock.p() as MockAuthUser | null;
          if (persisted) {
            const u = toAuthenticatedUser(persisted);
            const token = mock.t(persisted);
            setUser(u);
            setToken(token);
            setCurrentToken(token);
            return;
          }
          setCurrentToken(null);
          return;
        }
        const manager = userManagerRef;
        if (!manager) return;
        const u = await manager.getUser();
        if (!active) return;
        if (u && !u.expired) {
          setUser(toAuthenticatedUser(u));
          setToken(u.access_token);
          setCurrentToken(u.access_token);
          authStore().setItem(SESSION_STORAGE_KEY, JSON.stringify(u));
        } else {
          setCurrentToken(null);
        }
      } catch (err) {
        console.error('ZitadelProvider init failed', err);
      } finally {
        if (active) setIsLoading(false);
      }
    }
    void init();
    return () => {
      active = false;
    };
  }, [authMode, userManagerRef]);

  const login = useCallback(
    async (returnTo?: string) => {
      if (authMode === 'mock') {
        if (!isMockAuthAvailable()) {
          throw new Error('mock-auth is not available in production builds');
        }
        // Default to site_supervisor when no role is preselected in mock mode.
        const mock = await loadMockAuth();
        const mockUser = mock.g('nexsoss:site_supervisor') as MockAuthUser;
        mock.s(mockUser);
        const token = mock.t(mockUser);
        const u = toAuthenticatedUser(mockUser);
        setUser(u);
        setToken(token);
        setCurrentToken(token);
        window.location.assign(safeReturnTo(returnTo));
        return;
      }
      const manager = userManagerRef;
      if (!manager) throw new Error('OIDC UserManager not initialised');
      const target = safeReturnTo(returnTo);
      // Stashed BEFORE the redirect, which leaves the page.
      stashReturnTo(target);
      await manager.signinRedirect({ state: target });
    },
    [authMode, userManagerRef],
  );

  const logout = useCallback(async () => {
    if (authMode === 'mock') {
      const mock = isMockAuthAvailable() ? await loadMockAuth() : null;
      if (mock) mock.s(null);
      setUser(null);
      setToken(null);
      setCurrentToken(null);
      window.location.assign('/login');
      return;
    }
    const manager = userManagerRef;
    if (!manager) return;
    // A return path belongs to the person who asked for it; the next
    // person to sign in on this tab must not land on it.
    clearReturnToStash();
    await manager.signoutRedirect();
    // BOTH stores, not just the active one: the same person on the
    // same device produces sessions in each depending on whether they
    // opened a tab or the installed app, and a sign-out that leaves a
    // token behind in the other is the one outcome it must not have.
    clearStoredSession(SESSION_STORAGE_KEY);
    setCurrentToken(null);
  }, [authMode, userManagerRef]);

  const signInMockRole = useCallback(
    (role: RoleToken) => {
      if (authMode !== 'mock') return;
      if (!isMockAuthAvailable()) return;
      // Synchronous path for the role-picker UI: we already loaded
      // mock-auth once via the init effect, so loadMockAuth() returns
      // the cached module without an extra round-trip. If the cache
      // is empty for some reason, we fall back to a no-op rather than
      // throwing — the LoginScreen exposes the same fallback via the
      // role buttons.
      void loadMockAuth().then((mock) => {
        const mockUser = mock.i(role) as MockAuthUser;
        const token = mock.t(mockUser);
        setUser(toAuthenticatedUser(mockUser));
        setToken(token);
        setCurrentToken(token);
      });
    },
    [authMode],
  );

  // INV-7 — token-expiry reconnect. Schedule a single `setTimeout` 60 s
  // before the JWT `exp` to disconnect + reconnect the Zenoh SSE
  // transport with a fresh token, so a near-expiry request is never
  // sent.
  //
  // A8 (deployment-readiness): if the refresh fails (the broker
  // rejects the new token, the OIDC refresh_token endpoint returns
  // 401, or the network drops), we surface a toast and redirect to
  // /login so the operator doesn't stay stuck on a stale token
  // that *looks* valid but can't authenticate against any
  // endpoint.
  useEffect(() => {
    if (!token) return;
    const exp = getJwtExp(token);
    if (exp === null) return;
    const REFRESH_LEAD_MS = 60_000;
    const delay = exp * 1000 - Date.now() - REFRESH_LEAD_MS;

    async function rotate() {
      try {
        await getRmfLive().rotateToken();
      } catch (err) {
        // Refresh failed. Force-clear the token (so any in-flight
        // HTTP request fails fast with 401), surface a toast, and
        // redirect to /login after 5 s so the operator can sign
        // back in.
        setCurrentToken(null);
         
        console.warn('[auth] token rotation failed', err);
        window.dispatchEvent(
          new CustomEvent('nexsoss:session-expired', {
            detail: { reason: 'token_rotation_failed', ts: Date.now() },
          }),
        );
        // Read when the redirect fires, not when the rotation failed: the
        // person may have moved in the 5 s between.
        const handle = window.setTimeout(() => {
          window.location.assign(loginUrlForCurrentPage());
        }, 5_000);
        return () => window.clearTimeout(handle);
      }
      return undefined;
    }

    let cleanup: (() => void) | undefined;
    if (delay <= 0) {
      void rotate().then((c) => {
        cleanup = c;
      });
    } else {
      const t = window.setTimeout(() => {
        void rotate().then((c) => {
          cleanup = c;
        });
      }, delay);
      cleanup = () => window.clearTimeout(t);
    }
    return () => {
      cleanup?.();
    };
  }, [token]);

  // Resolves to the path the sign-in should land on (see
  // `signinReturnPath`), or undefined when there is none to give.
  const completeSignin = useCallback(
    async (url?: string): Promise<string | void> => {
      if (authMode !== 'zitadel') return;
      const manager = userManagerRef;
      if (!manager) throw new Error('OIDC UserManager not initialised');
      try {
        const u = await manager.signinRedirectCallback(url);
        if (!u) throw new Error('No user returned from sign-in callback');
        setUser(toAuthenticatedUser(u));
        setToken(u.access_token);
        setCurrentToken(u.access_token);
        authStore().setItem(SESSION_STORAGE_KEY, JSON.stringify(u));
        return signinReturnPath(u.state);
      } catch (err) {
        // In React StrictMode the effect can run twice after the OAuth state
        // has already been consumed. Fall back to the stored user before failing.
        const stored = await manager.getUser();
        if (stored && !stored.expired) {
          setUser(toAuthenticatedUser(stored));
          setToken(stored.access_token);
          setCurrentToken(stored.access_token);
          authStore().setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
          // A stored user never carries `state`, so only the stash knows.
          return signinReturnPath(undefined);
        }
        throw err;
      }
    },
    [authMode, userManagerRef],
  );

  const value: AuthContextValue = {
    isLoading,
    isAuthenticated: user !== null,
    user,
    roles: user?.roles ?? [],
    token,
    login,
    signInMockRole,
    completeSignin,
    logout,
    authMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

